import { Component } from '@theme/component';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';

/**
 * Header chrome: keeps `--header-height` in sync with the rendered header (Horizon's sticky
 * add-to-cart, product details and cart summary consume it), mirrors the mobile menu's
 * open state onto its trigger, and announces cart count changes to screen readers.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion
 *
 * @extends {Component<Refs>}
 */
class DtHeaderComponent extends Component {
  requiredRefs = ['liveRegion'];
  #controller = new AbortController();
  #wide = window.matchMedia('(min-width: 750px)');

  #resizeObserver = new ResizeObserver(() => this.#measure());

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    this.#measure();
    // Link widths change when the mono face swaps in, without resizing the header.
    document.fonts.ready.then(() => this.#measure());
    this.#resizeObserver.observe(this);
    const announcement = document.querySelector('#header-group .announcement-bar');
    if (announcement) this.#resizeObserver.observe(announcement);
    // A drawer restored open across a navigation never fires DrawerOpenEvent.
    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      this.querySelector(`[aria-controls="${drawer.id}"]`)?.setAttribute('aria-expanded', 'true');
    }
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerToggle, { signal });
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerToggle, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    // The menu button is phone-only: a menu left open while the window widens closes with it.
    this.#wide.addEventListener(
      'change',
      (event) => {
        if (event.matches) /** @type {any} */ (document.getElementById('dt-menu'))?.close?.();
      },
      { signal }
    );
  }

  /** Header alone drives sticky offsets; header + trust strip drives first-fold heights. */
  #measure() {
    // Any merchant menu fits: when the links overflow the row, CSS swaps them for the drawer button.
    const nav = this.querySelector('.dt-header__nav');
    if (nav) this.toggleAttribute('data-nav-overflow', nav.scrollWidth > nav.clientWidth);
    const group = document.getElementById('header-group');
    const total = group ? [...group.children].reduce((sum, el) => sum + el.offsetHeight, 0) : this.offsetHeight;
    document.body.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#controller.abort();
  }

  /**
   * The Bag link is a plain link to /cart; with JS and a drawer on the page it opens the drawer instead.
   * Modified clicks (new tab, new window) keep the link behaviour.
   * @param {MouseEvent} event
   */
  openCart(event) {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!drawer?.toggle || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    drawer.toggle();
  }

  /** @param {Event} event */
  #onDrawerToggle = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.id) return;
    const trigger = this.querySelector(`[aria-controls="${event.target.id}"]`);
    trigger?.setAttribute('aria-expanded', String(event.type === DrawerOpenEvent.eventName));
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        const trigger = /** @type {HTMLElement | null} */ (this.querySelector('.dt-header__cart'));
        if (trigger) {
          const { labelOne = '', labelOther = '' } = trigger.dataset;
          trigger.setAttribute('aria-label', count === 1 ? labelOne : labelOther.replace('%d', String(count)));
        }
        // The add button and the auto-opened drawer already announce the add; stay quiet then and on errors.
        // ponytail: below 990px the modal drawer makes this region inert, so an add is heard only as the drawer opening.
        if (
          detail?.didError ||
          (event.action === 'add' && document.getElementById('cart-drawer')?.hasAttribute('open'))
        )
          return;
        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${count}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[dt-header] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('dt-header-component')) {
  customElements.define('dt-header-component', DtHeaderComponent);
}
